import { useEffect, useMemo, useState } from 'react';
import { Search, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_LABELS } from '../../lib/viewAs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const GROUPS = [
  { key: 'surveyor', label: 'Surveyors', match: (u) => (u.roles || []).includes('surveyor') },
  { key: 'operative', label: 'Operatives', match: (u) => u.user_type !== 'sub_contractor' && (u.roles || []).includes('operative') },
  { key: 'sub_contractor', label: 'Sub-contractors', match: (u) => u.user_type === 'sub_contractor' },
  { key: 'other', label: 'Other logins', match: () => true },
];

const initials = (n) => (n || '?').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
const labelFor = (u) => (u.user_type === 'sub_contractor' ? ROLE_LABELS.sub_contractor : ROLE_LABELS[u.role] || ROLE_LABELS[u.user_type] || u.role);

/** Pick a non-admin login to view the app as. Admin accounts are never listed (and blocked server-side). */
export function ImpersonateDialog({ open, onOpenChange }) {
  const { api, startViewAs } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setLoading(true);
    api.get('/admin/impersonation/users').then((r) => setUsers(r.data || [])).catch(() => toast.error('Could not load users')).finally(() => setLoading(false));
  }, [open, api]);

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const left = users.filter((u) => !needle || `${u.name} ${u.email} ${u.trade || ''}`.toLowerCase().includes(needle));
    const seen = new Set();
    return GROUPS.map((g) => ({ ...g, items: left.filter((u) => { if (seen.has(u.id) || !g.match(u)) return false; seen.add(u.id); return true; }) })).filter((g) => g.items.length);
  }, [users, q]);

  const start = async (u) => {
    setStarting(u.id);
    try { await startViewAs(u.id); } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); setStarting(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2A3240] border-white/10 text-white sm:max-w-lg p-0 overflow-hidden" data-testid="impersonate-dialog">
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#F5A623]" /> View the app as a user</DialogTitle>
          <DialogDescription className="text-gray-400">
            See exactly what they see — their projects, diary, jobs and permissions. Read-only until you allow edits. Admin accounts can’t be impersonated. The session is logged.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or trade" autoFocus className="pl-9 bg-[#1E2530] border-white/10 text-white" data-testid="impersonate-search" />
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto px-3 pb-4" data-testid="impersonate-user-list">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>}
          {!loading && !grouped.length && <p className="text-sm text-gray-500 text-center py-10">No matching logins.</p>}
          {grouped.map((g) => (
            <div key={g.key} className="mb-3">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-3 mb-1">{g.label}</p>
              {g.items.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" data-testid={`impersonate-user-${u.id}`}>
                  <div className="w-9 h-9 rounded-full bg-[#F5A623]/15 text-[#F5A623] text-xs font-bold flex items-center justify-center shrink-0">{initials(u.name)}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-gray-500 truncate">{u.email}{u.trade ? ` · ${u.trade}` : ''}</p>
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-gray-300 shrink-0">{labelFor(u)}</span>
                  <Button size="sm" onClick={() => start(u)} disabled={!!starting} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-8 shrink-0" data-testid={`impersonate-start-${u.id}`} data-imp-safe>
                    {starting === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Eye className="w-3.5 h-3.5 mr-1.5" /> View as</>}
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
